import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@timeghost/ui/shadcn/alert-dialog";
import { Input } from "@timeghost/ui/shadcn/input";
import { Label } from "@timeghost/ui/shadcn/label";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, JSX, ReactNode } from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import CopyButton from "@/components/ui/copy-button";
import { cn } from "@/lib/utils";

const CONFIRM_COPY_BUTTON_CLASS = "min-w-0 max-w-full justify-start overflow-hidden font-mono";

const confirmDialogContentVariants = cva("", {
	variants: {
		size: {
			sm: "max-w-xs data-[size=sm]:max-w-xs sm:max-w-sm data-[size=sm]:sm:max-w-sm",
			default: "max-w-md data-[size=default]:max-w-md sm:max-w-lg data-[size=default]:sm:max-w-lg",
			md: "max-w-md data-[size=md]:max-w-md sm:max-w-lg data-[size=md]:sm:max-w-lg",
		},
	},
	defaultVariants: {
		size: "sm",
	},
});

interface ConfirmDialogRootProps extends VariantProps<typeof confirmDialogContentVariants> {
	readonly cancelDisabled?: boolean;
	readonly cancelLabel: ReactNode;
	readonly children?: ReactNode;
	readonly className?: string;
	readonly confirmDisabled?: boolean;
	readonly confirmLabel: ReactNode;
	/** When set, confirm stays disabled until this string is typed exactly. */
	readonly confirmText?: string;
	readonly confirmTextLabel?: ReactNode;
	readonly description: ReactNode;
	readonly onConfirm: () => void;
	readonly onOpenChange?: (open: boolean) => void;
	readonly onOpenChangeComplete?: (open: boolean) => void;
	readonly open?: boolean;
	readonly pending?: boolean;
	readonly title: ReactNode;
	readonly variant?: "default" | "destructive" | "outline";
}

interface ConfirmTypeFieldProps {
	readonly disabled: boolean;
	readonly expected: string;
	readonly id: string;
	readonly invalid: boolean;
	readonly label?: ReactNode;
	readonly onSubmit: () => void;
	readonly onTypedChange: (value: string) => void;
	readonly typed: string;
}

interface ConfirmTypeDefaultInstructionProps {
	readonly expected: string;
}

function ConfirmTypeDefaultInstruction({ expected }: ConfirmTypeDefaultInstructionProps): JSX.Element {
	const { t } = useTranslation();
	return (
		<>
			<span>{t("shell.confirm.typeToConfirmLead")}</span>
			<CopyButton className={CONFIRM_COPY_BUTTON_CLASS} size="xs" value={expected}>
				<span className="min-w-0 truncate">{expected}</span>
			</CopyButton>
			<span>{t("shell.confirm.typeToConfirmTrail")}</span>
		</>
	);
}

function ConfirmTypeField({
	disabled,
	expected,
	id,
	invalid,
	label,
	onSubmit,
	onTypedChange,
	typed,
}: ConfirmTypeFieldProps): JSX.Element {
	const { t } = useTranslation();

	return (
		<div className="grid w-full gap-2 text-left">
			<div className="grid h-auto min-w-0 justify-items-start gap-1.5 font-normal text-sm leading-relaxed">
				{label ? label : <ConfirmTypeDefaultInstruction expected={expected} />}
			</div>
			<Label className="sr-only" htmlFor={id}>
				{`${t("shell.confirm.typeToConfirmLead")} ${expected} ${t("shell.confirm.typeToConfirmTrail")}`}
			</Label>
			<Input
				aria-invalid={invalid}
				autoComplete="off"
				autoCorrect="off"
				autoFocus
				disabled={disabled}
				id={id}
				onChange={(event) => {
					onTypedChange(event.target.value);
				}}
				onKeyDown={(event) => {
					if (event.key !== "Enter") {
						return;
					}
					event.preventDefault();
					onSubmit();
				}}
				placeholder={expected}
				spellCheck={false}
				value={typed}
			/>
		</div>
	);
}

function ConfirmDialogRoot({
	className,
	open,
	onOpenChange,
	onOpenChangeComplete,
	title,
	description,
	confirmLabel,
	cancelLabel,
	confirmText,
	confirmTextLabel,
	onConfirm,
	pending = false,
	confirmDisabled = false,
	cancelDisabled = false,
	variant = "destructive",
	size,
	children,
}: ConfirmDialogRootProps): JSX.Element {
	const confirmFieldId = useId();
	const expected = confirmText ?? "";
	const requireMatch = expected.length > 0;
	const [typed, setTyped] = useState("");
	const resetKey = `${String(open)}:${expected}`;
	const [seenResetKey, setSeenResetKey] = useState(resetKey);
	if (seenResetKey !== resetKey) {
		setSeenResetKey(resetKey);
		setTyped("");
	}
	const matches = !requireMatch || typed === expected;
	const canConfirm = matches && !confirmDisabled;
	const resolvedSize = size ?? (requireMatch ? "md" : "sm");
	const tryConfirm = (): void => {
		if (!canConfirm) {
			return;
		}
		onConfirm();
	};

	return (
		<AlertDialog onOpenChange={onOpenChange} onOpenChangeComplete={onOpenChangeComplete} open={open}>
			{children}
			<AlertDialogContent
				className={cn(confirmDialogContentVariants({ size: resolvedSize }), className)}
				size={resolvedSize}>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{description}</AlertDialogDescription>
				</AlertDialogHeader>
				{requireMatch ? (
					<ConfirmTypeField
						disabled={pending}
						expected={expected}
						id={confirmFieldId}
						invalid={typed.length > 0 && !matches}
						label={confirmTextLabel}
						onSubmit={tryConfirm}
						onTypedChange={setTyped}
						typed={typed}
					/>
				) : null}
				<AlertDialogFooter>
					<AlertDialogCancel disabled={cancelDisabled || pending}>{cancelLabel}</AlertDialogCancel>
					<AlertDialogAction disabled={!canConfirm} loading={pending} onClick={tryConfirm} variant={variant}>
						{confirmLabel}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

ConfirmDialogRoot.displayName = "ConfirmDialog";

function ConfirmDialogTrigger(props: ComponentProps<typeof AlertDialogTrigger>): JSX.Element {
	return <AlertDialogTrigger nativeButton={false} {...props} />;
}

ConfirmDialogTrigger.displayName = "ConfirmDialog.Trigger";

const ConfirmDialog = Object.assign(ConfirmDialogRoot, {
	Trigger: ConfirmDialogTrigger,
});

export default ConfirmDialog;
